import React, { FC, MouseEventHandler, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DropResult } from 'react-beautiful-dnd';
import { cloneDeep } from 'lodash';
import { ITrajectoryDetail, PageType } from '../../../../../../config/api/models/Trajectory';
import apiRoutes from '../../../../../../config/api/routes';
import { Button, Icons, Paragraph } from '../../../../../shared/components';
import { useFetch, useUser } from '../../../../../shared/hooks';
import { AddBlockButtonGroup } from '../../AddBlockButton';
import BlocksList from './BlocksList';
import { useAppDispatch, useAppSelector } from '../../../../../shared/hooks/useAppDispatch';
import { Theme, styled } from '@material-ui/core';
import { DifferentiationsOverviewWrapper } from '../../../Differentiation/Atoms';
import { updateBlocksList } from 'src/modules/shared/slices/blocks/entity/BlocksSlice';
import { mapBlocksListToIds } from 'src/config/api/models/Block';

type Props = {
  type: PageType;
  trajectory: ITrajectoryDetail;
  bookId: string;
  pageId?: string;
  onPublish?: (id: string, title: string) => void;
  onUnPublish?: (id: string, title: string) => void;
};

const PageWrapper = styled('div')<Theme>(({ theme }) => ({
  position: 'relative',
  padding: '3rem 1.5rem 1.5rem 1.5rem',
  background: theme.palette.grey['50'],
  border: `1px dashed ${theme.palette.grey['200']}`,
}));

const Legend = styled('div')<Theme>(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: '1.5rem',
  transform: 'translateY(-50%)',
  background: theme.palette.common.white,
  boxShadow: theme.config.defaultBoxShadow,
  fontFamily: theme.config.secondaryFontFamily,
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.grey['500'],
  padding: theme.spacing(0.5, 1),
}));

const PublishButtonWrapper = styled('div')({
  paddingBottom: '3rem',
});

export const PageManagementSidebarContent: FC<Props> = ({
  type,
  bookId,
  pageId,
  trajectory,
  onPublish,
  onUnPublish,
}) => {
  const { t } = useTranslation();
  const { isAuthor } = useUser();

  const [orderBlocksResponse, orderBlocks, resetOrderBlocks] = useFetch('', {
    method: 'PATCH',
  });

  const blocks = useAppSelector((state) => state.blocks.blocks);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (orderBlocksResponse.success) {
      resetOrderBlocks();
    }
  }, [orderBlocksResponse.success, resetOrderBlocks, blocks]);

  const handleOrderBlocks = ({ source, destination }: DropResult) => {
    if (pageId != null && blocks != null && !!destination) {
      const currentBlocksList = [...blocks];
      const block = currentBlocksList[source.index];
      const newBlockIdList = cloneDeep(currentBlocksList);
      newBlockIdList.splice(source.index, 1);
      newBlockIdList.splice(destination.index, 0, block);
      const listIds = mapBlocksListToIds(newBlockIdList);

      orderBlocks({
        path: apiRoutes.orderBlocks(bookId, pageId),
        body: listIds,
      });
      dispatch(updateBlocksList(newBlockIdList));
    }
  };
  return pageId != null ? (
    <DifferentiationsOverviewWrapper>
      <Paragraph>{t('trajectory.sidebar.pageManagement.intro')}</Paragraph>
      {!!trajectory && isAuthor && (
        <PublishButtonWrapper>
          {trajectory.isDraft ? (
            <Button
              icon={Icons.PUBLISH_DRAFT}
              onClick={onPublish as unknown as MouseEventHandler<HTMLButtonElement>}
            >
              {t('books.chapter.trajectories.publish')}
            </Button>
          ) : (
            <Button
              icon={Icons.UN_PUBLISH_DRAFT}
              onClick={onUnPublish as unknown as MouseEventHandler<HTMLButtonElement>}
            >
              {t('books.chapter.trajectories.unPublish')}
            </Button>
          )}
        </PublishButtonWrapper>
      )}
      {blocks && (
        <PageWrapper>
          <Legend>{t('trajectory.page.label').toUpperCase()}</Legend>
          <BlocksList
            trajectoryId={trajectory.id}
            bookId={bookId}
            pageType={type}
            onOrderBlocks={handleOrderBlocks}
          />
          <AddBlockButtonGroup pageId={pageId} previousBlockId={blocks[blocks.length - 1]?.id} />
        </PageWrapper>
      )}
    </DifferentiationsOverviewWrapper>
  ) : null;
};
